// Leaderboard (gamification): "Top performers" - the lowest- and highest-mortality Barns
// across the whole Directory right now. A lightweight motivational nudge, not a new metric of its
// own - every figure it ranks is already computed by the live overview query, this just sorts and
// caps it.
//
// Ranks Barns, not Sections and not Farms. A Barn always carries its own `currentValue` regardless of
// whether it's expandable (pooled across its Sections) or aggregated (computed directly - see
// BarnObjectiveRow), so ranking uniformly at Barn level needs no new aggregation and can't double-
// count a Section's pigs against its own Barn's rolled-up figure. Ranking literal Farms would need a
// new weighted (dead/avgPigs) aggregate the frontend doesn't have the inputs for, so Barn-level is
// both simpler and more actionable (names the specific unit, not just which Farm it's in).
//
// Barns with zero recorded deaths are dropped: a real 0%-mortality Barn is indistinguishable from one
// whose data extract just hasn't been refreshed yet - a leaderboard a stale Barn could top would
// reward the wrong thing.
import type { FarmObjectiveGroupRow } from './objectiveRow';

export type LeaderboardEntry = {
    farmId: number;
    farmName: string;
    barnId: number;
    barnName: string;
    currentValue: number;
    // Carried through so the modal can colour the figure by deviation, not by rank - the lowest
    // mortality Directory-wide can still be over its own Barn's target and has to read red for that,
    // "top performer" notwithstanding.
    resolvedTarget: number | null;
    deadCount: number;
};

export type LeaderboardSplit = {
    best: LeaderboardEntry[];
    worst: LeaderboardEntry[];
};

const BEST_SIZE = 5;
// Worst list is thresholded rather than a flat size: below MANY_THRESHOLD qualifying Barns, a 5-deep
// Worst list next to a 5-deep Best one would eat most or all of a small Directory's own data, so it's
// capped at WORST_SIZE_SMALL until there's a comfortably larger pool to draw a distinct "worst" tail
// from.
const WORST_SIZE_LARGE = 5;
const WORST_SIZE_SMALL = 3;
const MANY_THRESHOLD = 10;

function toQualifyingEntries(farms: FarmObjectiveGroupRow[]): LeaderboardEntry[] {
    return farms.flatMap((farm) =>
        farm.barns
            .filter((barn) => barn.currentValue != null && barn.deadCount > 0)
            .map((barn) => ({
                farmId: farm.id,
                farmName: farm.name,
                barnId: barn.id,
                barnName: barn.name,
                currentValue: barn.currentValue as number,
                resolvedTarget: barn.resolvedTarget,
                deadCount: barn.deadCount,
            }))
    );
}

// Best and Worst together, not two independent calls - the two lists must be mutually exclusive,
// never repeating a Barn. Worst is drawn from whatever qualifying Barns are left once Best has taken
// its slice, so the exclusivity is structural rather than something callers have to remember to
// dedupe themselves. An empty `worst` array (Best consumed the entire qualifying pool) is a valid,
// expected result - the modal renders an explanatory note for that case rather than a blank list.
export function computeLeaderboardSplit(farms: FarmObjectiveGroupRow[]): LeaderboardSplit {
    const qualifying = toQualifyingEntries(farms);
    const best = [...qualifying].sort((a, b) => a.currentValue - b.currentValue).slice(0, BEST_SIZE);

    // location_id is globally unique across Farm/Barn/Batch/Section, so barnId alone is a safe key
    // here - no risk of two different Farms' Barns colliding.
    const bestKeys = new Set(best.map((entry) => entry.barnId));
    const remaining = qualifying.filter((entry) => !bestKeys.has(entry.barnId));
    const worstSize = qualifying.length >= MANY_THRESHOLD ? WORST_SIZE_LARGE : WORST_SIZE_SMALL;
    const worst = remaining.sort((a, b) => b.currentValue - a.currentValue).slice(0, worstSize);

    return { best, worst };
}

// Below this many Barns Directory-wide, a leaderboard has too small a field to rank meaningfully.
export const MIN_BARNS_FOR_LEADERBOARD = 3;

export type LeaderboardDisabledReason = 'minBarns' | 'noData' | null;

// Gates the leaderboard toolbar button's usability, not its presence — the caller keeps rendering
// the button either way and shows this as a tooltip explaining which condition is unmet. Counted in
// Barns, not Farms: this leaderboard ranks Barns (see computeLeaderboardSplit above), so a single
// Farm with five Barns is already a real field of competitors, while five Farms with one Barn each
// would only be five. Only an empty Best matters for `noData` — an empty Worst is a normal state
// (Best consumed the whole qualifying pool), not a "nothing to show" one.
export function resolveLeaderboardDisabledReason(totalBarnCount: number, bestCount: number): LeaderboardDisabledReason {
    if (totalBarnCount < MIN_BARNS_FOR_LEADERBOARD) return 'minBarns';
    if (bestCount === 0) return 'noData';
    return null;
}
